import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useCallback, useEffect, useState } from "react";
import { foyerApi } from "../http/foyer";
import { tokens } from "../theme";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  DayOfWeek,
  PowerSchedule,
  PowerScheduleRule,
} from "@shared/types/foyer";

interface Props {
  open: boolean;
  premiseId: string;
  onClose: () => void;
}

const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

const BROWSER_TZ =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : "UTC";

function ruleSummary(r: PowerScheduleRule): string {
  const days =
    r.days.length === 7
      ? "every day"
      : r.days.map((d) => DAY_LABEL[d]).join(" ");
  return `${r.action === "on" ? "On" : "Off"} ${r.time} · ${days || "no days"}`;
}

function DayPicker({
  value,
  onChange,
}: {
  value: DayOfWeek[];
  onChange: (next: DayOfWeek[]) => void;
}) {
  return (
    <ToggleButtonGroup
      size="small"
      value={value}
      onChange={(_, next: DayOfWeek[]) => onChange(next)}
    >
      {DAYS_OF_WEEK.map((d) => (
        <ToggleButton key={d} value={d} sx={{ px: 1.25 }}>
          {DAY_LABEL[d]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

interface DraftForm {
  name: string;
  timezone: string;
  rules: PowerScheduleRule[];
  enabled: boolean;
}

function emptyDraft(): DraftForm {
  return {
    name: "",
    timezone: BROWSER_TZ,
    rules: [
      { days: [...DAYS_OF_WEEK], time: "23:00", action: "off" },
      { days: [...DAYS_OF_WEEK], time: "07:00", action: "on" },
    ],
    enabled: true,
  };
}

export default function PowerScheduleDialog({
  open,
  premiseId,
  onClose,
}: Props) {
  const [schedules, setSchedules] = useState<PowerSchedule[]>([]);
  const [error, setError] = useState<string | null>(null);
  // null = list view; otherwise the editor is open (with a draft).
  const [editing, setEditing] = useState<{
    id: string | null;
    draft: DraftForm;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  // How many of this premise's displays can actually obey a schedule. A device
  // whose shell has no power backend silently ignores the `power` command, so
  // scheduling it would look configured while changing nothing (and earning no
  // energy saving). Counted here so the dialog says so up front.
  const [coverage, setCoverage] = useState<{
    total: number;
    controllable: number;
  } | null>(null);

  const load = useCallback(async () => {
    if (!premiseId) return;
    try {
      const res = await foyerApi.listPowerSchedules(premiseId);
      setSchedules(res.schedules);
      setError(null);
    } catch {
      setError("Failed to load power schedules.");
    }
    try {
      const fleet = await foyerApi.fleetDevices({ premiseId });
      setCoverage({
        total: fleet.devices.length,
        controllable: fleet.devices.filter((d) =>
          d.live?.capabilities?.includes("powerControl")
        ).length,
      });
    } catch {
      // Non-fatal: the schedules still load, we just can't show coverage.
      setCoverage(null);
    }
  }, [premiseId]);

  useEffect(() => {
    if (open) {
      setEditing(null);
      void load();
    }
  }, [open, load]);

  function startCreate() {
    setEditing({ id: null, draft: emptyDraft() });
  }

  function startEdit(s: PowerSchedule) {
    setEditing({
      id: s.scheduleId,
      draft: {
        name: s.name,
        timezone: s.timezone,
        rules: s.rules.map((r) => ({ ...r })),
        enabled: s.enabled,
      },
    });
  }

  function setDraft(patch: Partial<DraftForm>) {
    setEditing((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, ...patch } } : prev
    );
  }

  function setRule(i: number, patch: Partial<PowerScheduleRule>) {
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            draft: {
              ...prev.draft,
              rules: prev.draft.rules.map((r, idx) =>
                idx === i ? { ...r, ...patch } : r
              ),
            },
          }
        : prev
    );
  }

  const save = useCallback(async () => {
    if (!editing) return;
    const { id, draft } = editing;
    const payload = {
      name: draft.name.trim(),
      timezone: draft.timezone.trim() || "UTC",
      rules: draft.rules,
      enabled: draft.enabled,
    };
    setSaving(true);
    setError(null);
    try {
      if (id) {
        await foyerApi.updatePowerSchedule(premiseId, id, payload);
      } else {
        await foyerApi.createPowerSchedule(premiseId, payload);
      }
      setEditing(null);
      await load();
    } catch {
      setError("Failed to save the schedule.");
    } finally {
      setSaving(false);
    }
  }, [editing, premiseId, load]);

  async function toggleEnabled(s: PowerSchedule) {
    try {
      await foyerApi.updatePowerSchedule(premiseId, s.scheduleId, {
        enabled: !s.enabled,
      });
      await load();
    } catch {
      setError("Failed to update the schedule.");
    }
  }

  async function remove(s: PowerSchedule) {
    if (!window.confirm(`Delete schedule “${s.name}”?`)) return;
    try {
      await foyerApi.deletePowerSchedule(premiseId, s.scheduleId);
      await load();
    } catch {
      setError("Failed to delete the schedule.");
    }
  }

  const draftValid = editing != null && editing.draft.name.trim() !== "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">Power schedules</Typography>
            <Typography variant="body2" color="text.secondary">
              Turn this premise&apos;s displays off out of hours to save energy.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {coverage && coverage.total > 0 && coverage.controllable === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="power-coverage">
            None of this premise&apos;s {coverage.total} displays report display
            power control, so a schedule here will not switch anything off and
            will not produce an energy saving. Check the devices&apos; shell
            version before relying on these figures.
          </Alert>
        )}
        {coverage &&
          coverage.controllable > 0 &&
          coverage.controllable < coverage.total && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="power-coverage">
              {coverage.controllable} of {coverage.total} displays support power
              control. The other {coverage.total - coverage.controllable} will
              ignore this schedule and keep drawing full power.
            </Alert>
          )}

        {editing ? (
          /* ── Editor ─────────────────────────────────────────────── */
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={editing.draft.name}
              onChange={(e) => setDraft({ name: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Timezone (IANA)"
              value={editing.draft.timezone}
              onChange={(e) => setDraft({ timezone: e.target.value })}
              helperText="Rule times are local to this timezone, e.g. Europe/Stockholm."
              fullWidth
            />
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">On/off rules</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setDraft({
                      rules: [
                        ...editing.draft.rules,
                        {
                          days: [...DAYS_OF_WEEK],
                          time: "23:00",
                          action: "off",
                        },
                      ],
                    })
                  }
                >
                  Add rule
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {editing.draft.rules.map((r, i) => (
                  <Card key={i} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={r.action}
                          onChange={(e) =>
                            setRule(i, {
                              action: e.target.value as "on" | "off",
                            })
                          }
                        >
                          <MenuItem value="off">Off</MenuItem>
                          <MenuItem value="on">On</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="time"
                        value={r.time}
                        onChange={(e) => setRule(i, { time: e.target.value })}
                        sx={{ width: 130 }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <IconButton
                        size="small"
                        aria-label="Remove rule"
                        onClick={() =>
                          setDraft({
                            rules: editing.draft.rules.filter(
                              (_, idx) => idx !== i
                            ),
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <DayPicker
                        value={r.days}
                        onChange={(days) => setRule(i, { days })}
                      />
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={editing.draft.enabled}
                  onChange={(e) => setDraft({ enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Stack>
        ) : schedules.length === 0 ? (
          /* ── Empty state ────────────────────────────────────────── */
          <Card sx={{ p: 5, textAlign: "center", color: tokens.inkMuted }}>
            <PowerSettingsNewIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography sx={{ mt: 1 }}>No power schedules yet.</Typography>
            <Typography variant="body2">
              Create one to power this premise&apos;s displays on/off
              automatically.
            </Typography>
          </Card>
        ) : (
          /* ── List ───────────────────────────────────────────────── */
          <Stack spacing={1.5}>
            {schedules.map((s) => (
              <Card
                key={s.scheduleId}
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {s.name}
                    </Typography>
                    {!s.enabled && (
                      <Chip
                        label="Disabled"
                        size="small"
                        sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {s.timezone}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {s.rules.map(ruleSummary).join("  ·  ") || "no rules"}
                  </Typography>
                </Box>
                <Switch
                  checked={s.enabled}
                  onChange={() => toggleEnabled(s)}
                  inputProps={{ "aria-label": "Toggle schedule enabled" }}
                />
                <IconButton
                  aria-label="Edit schedule"
                  onClick={() => startEdit(s)}
                >
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton
                  aria-label="Delete schedule"
                  onClick={() => remove(s)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!draftValid || saving}
              onClick={() => void save()}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startCreate}
            >
              New schedule
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
